import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { LocationType } from '~/client/graph'
import BaseReportButton, {
  IReportInfo,
} from '~/client/src/desktop/components/HeaderBar/components/BaseReportButton/BaseReportButton'
import { AdditionalReportFile } from '~/client/src/desktop/components/HeaderBar/HeaderBar.store'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { IAppConfig } from '~/client/src/shared/Config'
import { LogisticsFilterType } from '~/client/src/shared/enums/LogisticsFilterType'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import UIFilterInfo from '~/client/src/shared/stores/substates/UIFilterInfo'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import {
  ILocationFilterKeySummary,
  isLogisticWorkflow,
} from '~/client/src/shared/utils/logisticsHelper'
import { FORMS_REPORT_TEMPLATE_ID } from '~/client/src/shared/utils/usefulStrings'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import LogisticsStore from '../../Logistics/Logistics.store'
import LogisticsListStore from '../../LogisticsList/LogisticsList.store'

const DEFAULT_SORT_KEY = 'permit-name'

interface IProps {
  title: string
  isEnabled?: boolean
  logisticsStore: LogisticsStore
  logisticsListStore: LogisticsListStore
  getLocationFilterKeySummary: (key: string) => ILocationFilterKeySummary
  additionalReportFiles?: AdditionalReportFile[]
  className?: string
  state?: DesktopInitialState
  projectDateStore?: ProjectDateStore
  configuration?: IAppConfig
  toggleModal?: () => void
  shouldPreventCreation?: boolean
  shouldHideIcon?: boolean
  shouldInvertColors?: boolean
  companiesStore?: CompaniesStore
  workflowTypesStore?: WorkflowTypesStore
  projectMembersStore?: ProjectMembersStore
}

@inject(
  'state',
  'projectDateStore',
  'configuration',
  'companiesStore',
  'workflowTypesStore',
  'projectMembersStore',
)
@observer
export default class PermitReportButton extends React.Component<IProps> {
  public render() {
    const {
      className,
      title,
      toggleModal,
      shouldPreventCreation,
      shouldHideIcon,
      shouldInvertColors,
      isEnabled,
    } = this.props

    return (
      <BaseReportButton
        className={className}
        getReportInfo={this.getReportInfo}
        title={title}
        onClick={toggleModal}
        shouldPreventCreation={shouldPreventCreation}
        shouldHideIcon={shouldHideIcon}
        shouldInvertColors={shouldInvertColors}
        isEnabled={isEnabled}
      />
    )
  }

  private getReportInfo = (): IReportInfo => {
    const {
      logisticsStore: { startDate, endDate, isProjectCalendarModeActive },
      state,
      logisticsListStore: { groupingKey, sortState, filteredCollection },
      projectDateStore: { getMonthAndDayToDisplay, getClientTimezoneId },
      configuration: { TENANT_ID: tenantName },
      additionalReportFiles = [],
    } = this.props

    const {
      workflowsFilters: { fieldsMap },
      activeProject: {
        name: projectName,
        id: projectId,
        materialsUploadId,
        dateTimeFormat,
        logoUrl,
        isLogoWhite,
        code: projectCode,
      },
      user: { id: userId },
    } = state

    const startProjectDate = getMonthAndDayToDisplay(startDate)
    const endProjectDate = getMonthAndDayToDisplay(endDate)
    const isFullTime = isProjectCalendarModeActive
    const dateValue = isFullTime
      ? Localization.translator.fullProject
      : `${startProjectDate}-${endProjectDate}`

    const filename = `${tenantName}_${projectName}_${dateValue}`
    const filteredPermits = filteredCollection
      .filter(isLogisticWorkflow)
      .map(logistic => logistic.original)
    const filteredPermitIds = filteredPermits.map(permit => permit.id)

    const linkedPermitIds = filteredPermits.reduce((acc, workflow) => {
      workflow?.linkedWorkflowFields.forEach(id => acc.add(id))
      return acc
    }, new Set<string>())

    const filters = this.getPermitFilters(fieldsMap)
    const data = {
      projectId,
      materialsUploadId,
      logoUrl,
      isLogoWhite,
      userId,
      timezoneId: getClientTimezoneId(),
      dateTimeFormat,
      permitsFrom: startDate.getTime(),
      permitsTo: endDate.getTime(),
      filteredIds: {
        permits: filteredPermitIds,
      },
      linkedWorkflowIds: Array.from(linkedPermitIds),
      filters,
      grouping: {
        permits: groupingKey,
      },
      sorting: {
        permits: [
          {
            ascending: sortState.order !== SortOrder.DESC,
            key: sortState.columnKey || DEFAULT_SORT_KEY,
          },
        ],
      },
      qrCodeLinks: filteredPermitIds.map(permitId =>
        Workflow.getDirectLinkToInstance(projectCode, permitId),
      ),
    }

    return {
      projectId,
      dateFrom: startDate.getTime(),
      dateTo: endDate.getTime(),
      isFullTime,
      timezoneId: getClientTimezoneId(),
      templateId: FORMS_REPORT_TEMPLATE_ID,
      data,
      files: [
        ...additionalReportFiles
          .filter(file => file.isChecked)
          .map(file => ({
            fileId: file.value,
            fileName: `${filename}_${file.name}.${file.format.toLowerCase()}`,
          })),
      ],
      title: Localization.translator.workflowReportTitle,
    }
  }

  private getPermitFilters(fieldsMap: { [filterType: string]: UIFilterInfo }): {
    [filterType: string]: string[]
  } {
    const {
      getLocationFilterKeySummary,
      companiesStore: { getCompanyById },
      projectMembersStore,
      workflowTypesStore: { getLastUpdatedTypeByType },
    } = this.props
    const result: { [filterType: string]: string[] } = {}

    Object.values(LocationType).forEach(type => {
      result[type] = []
    })

    Object.entries(fieldsMap).forEach(([filterType, filter]) => {
      switch (filterType) {
        case LogisticsFilterType.Company:
          result[filterType] = Array.from(
            filter.selectedFilterOptions.keys(),
          ).map(id => getCompanyById(id)?.name)
          break
        case LogisticsFilterType.Status:
          result[filterType] = Array.from(filter.selectedFilterOptions.keys())
          break
        case LogisticsFilterType.Type:
          result[filterType] = Array.from(
            filter.selectedFilterOptions.keys(),
          ).map(type => getLastUpdatedTypeByType(type)?.name || type)
          break
        case LogisticsFilterType.Location:
        case LogisticsFilterType.Equipment:
          const keys: string[] = Array.from(
            filter.selectedFilterOptions.keys(),
          ).filter(key => key !== UNASSIGNED)
          keys.forEach(key => {
            const { type, name } = getLocationFilterKeySummary(key)
            result[type].push(name)
          })
          break
        case LogisticsFilterType.ResponsibleContact:
          result[filterType] = Array.from(
            filter.selectedFilterOptions.keys(),
          ).map(type => {
            const user = projectMembersStore.getById(type)
            return User.getFullName(user)
          })
          break
      }
    })

    return result
  }
}
